<script setup>
import { reactive, computed, onMounted, watch, onBeforeUnmount } from "vue";
import { useLoaderStore } from "@store/loader";
import { ElNotification } from "element-plus";
import UserApi from "@api/user.api";
import useForm from "@composables/Form";
import { useValidation } from "@composables/useValidation";
import { required, maxLength, email } from "@services/i18n-validators";

const props = defineProps({
    modelValue: { type: Boolean, default: false },
    userType: {
        type: String,
        default: "member",
        validator: (value) =>
            ["manager", "partner", "associate_partner", "member"].includes(
                value,
            ),
    },
});

const emit = defineEmits(["update:modelValue", "created"]);

const loaderStore = useLoaderStore();
let { formErrors } = useForm();

const user = reactive({
    name: "",
    email: "",
    company: "",
    designation: "",
    nationality: "",
    contractor: "",
});

const designationOptions = computed(() => {
    const options = [
        { id: "Partner(s)", name: "Partner(s)" },
        {
            id: "Associate Partner / Sr. Director / Director",
            name: "Associate Partner / Sr. Director / Director",
        },
        { id: "Manager / Senior Manager", name: "Manager / Senior Manager" },
        { id: "Team Member", name: "Team Member" },
    ];

    switch (props.userType) {
        case "manager":
            return options.filter((o) =>
                ["Manager / Senior Manager"].includes(o.name),
            );
        case "partner":
            return options.filter((o) => ["Partner(s)"].includes(o.name));
        case "associate_partner":
            return options.filter((o) =>
                ["Associate Partner / Sr. Director / Director"].includes(
                    o.name,
                ),
            );
        default:
            return options.filter((o) => ["Team Member"].includes(o.name));
    }
});

const rules = reactive({
    name: { required, maxLength: maxLength(50) },
    email: { required, email, maxLength: maxLength(255) },
    company: { required, maxLength: maxLength(50) },
    designation: { required },
    nationality: { required, maxLength: maxLength(50) },
    contractor: { required, maxLength: maxLength(50) },
});

const { v$, $externalResults } = useValidation(rules, user);

function resetForm() {
    Object.keys(user).forEach((key) => (user[key] = ""));
    formErrors.value = {};

    if ($externalResults?.value) $externalResults.value = {};
    if (v$?.value) v$.value.$reset();
}

function close() {
    resetForm();
    emit("update:modelValue", false);
}

async function submit() {
    const isValid = await v$.value.$validate();
    if (!isValid) return;

    try {
        loaderStore.start?.();

        const { data } = await UserApi.storeUser(user);

        ElNotification({
            title: "Success",
            message: data.message || "User created successfully",
            type: "success",
            position: "bottom-right",
        });

        const created = data?.data?.user || data?.data;
        emit("created", created);
        close();
    } catch (e) {
        const backendErrors = e?.response?.data?.message;

        ElNotification({
            title: "Error",
            message: backendErrors,
            type: "error",
            position: "bottom-right",
            dangerouslyUseHTMLString: true,
        });
    } finally {
        loaderStore.stop?.();
    }
}

watch(
    () => props.modelValue,
    (isOpen) => {
        if (isOpen) resetForm();
    },
    { immediate: true },
);

onBeforeUnmount(() => {
    document.body.classList.remove("suspend-wide-dialog");
});

onMounted(() => {
    const dialogTitle = `Add New External ${
        props.userType.charAt(0).toUpperCase() +
        props.userType.slice(1).replace("_", " ")
    }`;

    const dialogElement = document.querySelector(
        `div[aria-label="${dialogTitle}"]`,
    );

    if (dialogElement) {
        dialogElement.classList.add("el-overlay-dialog-c");
    }
});

const selectErrors = computed(() => {
    const result = { ...($externalResults?.value || {}) };
    const v = v$?.value;

    if (v?.designation?.$errors?.length) {
        result.designation = [v.designation.$errors[0].$message];
    }

    return result;
});
</script>

<template>
    <el-dialog
        :model-value="modelValue"
        :title="`Add New External ${userType.charAt(0).toUpperCase() + userType.slice(1).replace('_', ' ')}`"
        width="720px"
        class="request-doc-dialog user-dialog standalone-dialog"
        align-center
        @close="close"
        destroy-on-close
    >
        <div :style="{ maxHeight: '70vh', overflowY: 'scroll' }" class="row m-0">
            <!-- name -->
            <div class="col-md-12 p-0">
                <form-input
                    type="text"
                    v-model="user.name"
                    id="user_name"
                    label="Full Name"
                    :required="true"
                    :errors="v$?.name"
                    placeholder="Enter full name"
                />
            </div>

            <!-- email -->
            <div class="col-md-12 p-0">
                <form-input
                    type="email"
                    v-model="user.email"
                    id="user_email"
                    label="Email Address"
                    :required="true"
                    :errors="v$?.email"
                    placeholder="Enter email address"
                />
            </div>

            <!-- company -->
            <div class="col-md-12 p-0">
                <form-input
                    type="text"
                    v-model="user.company"
                    id="user_company"
                    label="Company"
                    :required="true"
                    :errors="v$?.company"
                    placeholder="Enter company name"
                />
            </div>

            <!-- designation -->
            <div class="col-md-12 mb-3 p-0">
                <form-el-select
                    name="designation"
                    :options="designationOptions"
                    label="Designation/Role"
                    :model="user"
                    :errors="selectErrors"
                    label-field="fields.designation"
                    :required="true"
                    @change="(val) => (user.designation = val)"
                    clearable
                    placeholder="Select designation"
                />
            </div>

            <!-- nationality -->
            <div class="col-md-12 p-0">
                <form-input
                    type="text"
                    v-model="user.nationality"
                    id="user_nationality"
                    label="Nationality"
                    :required="true"
                    :errors="v$?.nationality"
                    placeholder="Enter nationality"
                />
            </div>

            <!-- contractor -->
            <div class="col-md-12 p-0">
                <form-input
                    type="text"
                    v-model="user.contractor"
                    id="user_contractor"
                    label="Contractor"
                    :required="true"
                    :errors="v$?.contractor"
                    placeholder="Enter contractor name"
                />
            </div>
        </div>

        <template #footer>
            <div class="dialog-footer d-flex gap-2 justify-content-between">
                <button type="button" class="btn btn-light" @click="close">
                    {{ $t ? $t("messages.cancel") : "Cancel" }}
                </button>
                <button type="button" class="btn btn-primary" @click="submit">
                    {{ $t ? $t("messages.add") : "Add" }}
                </button>
            </div>
        </template>
    </el-dialog>
</template>

<style scoped>
.user-dialog :deep(.el-dialog) {
    border-radius: 16px;
    margin: 0 auto !important;
}

.user-dialog :deep(.el-dialog__header) {
    font-weight: 700;
}

.dialog-footer .btn {
    min-width: 120px;
    height: 44px;
    border-radius: 10px;
}

/* Center modal and constrain height with internal scrolling */
.user-dialog :deep(.el-dialog__wrapper) {
    display: flex;
    align-items: center;
    justify-content: center;
}

.user-dialog :deep(.el-dialog__body) {
    max-height: 70vh;
    overflow-y: auto;
}

/* Ensure this dialog ignores wide-modal global sizing */
.standalone-dialog :deep(.el-dialog) {
    width: 720px !important;
    max-width: 96vw !important;
    height: auto !important;
    margin: 0 auto !important;
}

.standalone-dialog :deep(.el-dialog__body) {
    height: auto !important;
    max-height: 70vh !important;
}

:deep(.el-overlay-dialog) {
    bottom: unset !important;
    padding: 10% 20%;
}
</style>
